import { DeleteIcon, DownloadIcon, EditIcon, ExternalLinkIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Group } from '@components/layout';
import { useConfirmation, useModal } from '@components/modal';
import { Typography } from '@components/typography';
import {
  GANDataset,
  useDeleteGANDatasetMutation,
  useDownloadDatasetMutation,
} from '@generated/graphql';
import { saveFile } from '@helper-functions/save-file';
import { Box, Grid } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import {
  DatasetsDetailsGanDatasetsEditModal,
  DatasetsDetailsMicroserviceStatus,
  useDatasets,
} from '@subsets/workspaces';

interface DatasetDetailsGanDatasetsListItemPreviewProps {
  ganDataset: Partial<GANDataset>;
  workspaceId: string;
  refetch: VoidFunction;
}

export const DatasetsDetailsGanDatasetsListItemPreview = ({
  ganDataset,
  workspaceId,
  refetch,
}: DatasetDetailsGanDatasetsListItemPreviewProps) => {
  const { status, name, model, description, datasetId, tags, parentId } = ganDataset || {};
  const { setSelectedDataset } = useDatasets();
  const handleSelectGanDataset = () => setSelectedDataset(datasetId);
  const { createAsyncNotification, addNotification, removeNotification } = useNotifications();
  const { showConfirmation } = useConfirmation();
  const [downloadDataset] = useDownloadDatasetMutation();
  const [deleteGANDatasetMutation] = useDeleteGANDatasetMutation();

  const { showModal: showEditGANDatasetModal } = useModal({
    component: DatasetsDetailsGanDatasetsEditModal,
    componentProps: {
      workspaceId,
      datasetId,
      datasetName: name || '',
      description: description || '',
      tags: tags || [],
      refetchGanDatasets: refetch,
    },
    modalProps: { title: 'Edit GAN Dataset' },
  });

  const startDownload = async () => {
    const key = `${Date.now() * 1000}.${Math.random() * 10000}`;
    try {
      addNotification({ text: 'Loading...', type: 'loading', key, duration: 100000 });
      const response = await downloadDataset({
        variables: {
          workspaceId,
          datasetId,
        },
      });

      saveFile({
        name: `${name || datasetId}.zip`,
        url: response?.data?.downloadDataset,
      });
      removeNotification(key);
      addNotification({
        text: 'Starting download...',
        type: 'success',
        duration: 6000,
      });
    } catch (e) {
      removeNotification(key);
      addNotification({
        text: (e as Error).message ?? 'Failed to download dataset',
        type: 'error',
        duration: 6000,
      });
    }
  };

  const handleDeleteGanDataset = () =>
    showConfirmation({
      onAffirm: createAsyncNotification('Successfully deleted GAN Dataset.', async () => {
        await deleteGANDatasetMutation({
          variables: {
            datasetId,
            workspaceId,
          },
        });
        refetch();
      }),
      message: `Are you sure you want to delete this GAN Dataset?`,
    });

  return (
    <Grid container alignItems="center" justifyContent="flex-start">
      <Grid item xs={1.5}>
        <Box display="flex" marginLeft={1}>
          <DatasetsDetailsMicroserviceStatus
            status={status}
            workspaceId={workspaceId}
            microServiceName="ganDatasets"
            microServiceId={datasetId}
            datasetId={parentId}
          />
        </Box>
      </Grid>
      <Grid item xs={3} paddingRight={3}>
        <Typography
          noWrap
          handleOverFlow
          variant="body2"
          tooltip={datasetId}
          tooltipProps={{ variant: 'secondary' }}
        >
          {name || '-'}
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Typography noWrap handleOverFlow variant="body2">
          {model || '-'}
        </Typography>
      </Grid>
      <Grid item lg={4.5}>
        <Group justifyContent="flex-end" gap={2}>
          {status === 'success' && (
            <>
              <IconButton
                onClick={handleSelectGanDataset}
                data-cy="GAN-Datasets-Create-Dataset-Link-Icon"
                Icon={ExternalLinkIcon}
              />
              <IconButton
                onClick={() => showEditGANDatasetModal()}
                data-cy="GAN-Datasets-Create-Dataset-Edit-Icon"
                Icon={EditIcon}
              />

              <IconButton
                onClick={startDownload}
                data-cy="GAN-Datasets-Create-Download-Link-Icon"
                Icon={DownloadIcon}
              />
            </>
          )}
          <IconButton
            onClick={handleDeleteGanDataset}
            data-cy="GAN-Datasets-Create-Delete-Link-Icon"
            Icon={DeleteIcon}
          />
        </Group>
      </Grid>
    </Grid>
  );
};
